<template>
  <section class="flat-scroll sonar-welcome">
    <div class="sonar-welcome__container">
      <h3 class="sonar-welcome__title">
        {{ assessmentName }}
      </h3>
      <div class="sonar-welcome__data-container">
        <div class="sonar-welcome__data-card">
          <h3 class="sonar-welcome__data-title">
            Labs:
          </h3>
          <h2 class="sonar-welcome__data-info">
            {{ assessmentAttempt.total_labs }}
          </h2>
        </div>
        <div class="sonar-welcome__data-card">
          <h3 class="sonar-welcome__data-title">
            Difficulty:
          </h3>
          <h2 class="sonar-welcome__data-info">
            {{ assessmentAttempt.difficulty }}
          </h2>
        </div>
        <div class="sonar-welcome__data-card">
          <h3 class="sonar-welcome__data-title">
            Duration:
          </h3>
          <h2 class="sonar-welcome__data-info">
            15m-20m
          </h2>
        </div>
      </div>
      <div class="sonar-welcome__info-container">
        <h4>
          <strong>
            <p>Overview</p>
            <p v-html="assessmentAttemptDescription"></p>
          </strong>
        </h4>
        <div v-if="topSkills.length">
          <h4><strong>Top skills</strong></h4>
          <div class="sonar-welcome__top-skill-container">
            <SonarTopSkillPill
              v-for="topSkill in topSkills"
              :key="topSkill"
              :topSkill="topSkill"
            />
          </div>
        </div>
        <div class="sonar-welcome__divider"></div>
        <h4><strong>Here's what you need to know before you start:</strong></h4>
        <h4>
          Assess your skills, find areas to grow, and discover the best training
          for you. Stay focused, don't overthink it, and most importantly, have
          fun!
        </h4>
        <ol class="sonar-welcome__infoList-description">
          <li v-for="item in infoItems" :key="item.title">
            <span>{{ item.title }}</span>
            <span class="sonar-welcome__infoItem-description">
              {{ item.description }}</span
            >
          </li>
        </ol>
        <p>Have fun and good luck!</p>
      </div>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import DOMPurify from 'dompurify'
import SonarTopSkillPill from '../SonarTopSkillPill.vue'

export default {
  name: 'SonarAssessmentWelcome',
  components: { SonarTopSkillPill },
  data: () => {
    return {
      infoItems: [
        {
          title: 'Lab Restrictions:',
          description:
            'You can run only one lab at a time. Before starting a new one, ensure no other labs are active in different browser windows.',
        },
        {
          title: 'Time Limit:',
          description:
            'This assessment should take 15 minutes or less to complete. However, you have 60 minutes if you need extra time to finish.',
        },
        {
          title: 'Answering Questions:',
          description:
            "If you're unsure of an answer, choose 'I don't know.' It won't negatively impact your score and helps us provide tailored content to enhance your skills.",
        },
        {
          title: 'Trust Your Instincts:',
          description:
            "We've intentionally omitted a back button to encourage you to trust your intuition and answer confidently.",
        },
        {
          title: 'Question Format:',
          description:
            "The assessment includes multiple-choice and scenario-based lab questions. You'll navigate the environment to answer questions.",
        },
        {
          title: 'Score Preservation:',
          description:
            'When you retake an assessment, your highest score will be safeguarded for 6 months, irrespective of any lower scores you may achieve',
        },
      ],
    }
  },
  computed: {
    ...mapGetters({
      assessmentAttempt: 'sonar/assessmentAttempt',
    }),
    assessmentName() {
      return this.assessmentAttempt?.assessment?.name
    },
    assessmentAttemptDescription() {
      const defaultDescription = `<p>Assess your skills, find areas to grow, and discover the best training for you. Stay focused, don't overthink it, and most importantly, have fun!</p>`
      const description = this.assessmentAttempt?.assessment?.description_html
      return description ? DOMPurify.sanitize(description) : defaultDescription
    },
    topSkills() {
      return this.assessmentAttempt?.assessment?.top_skills || []
    },
  },
}
</script>

<style lang="scss" scoped>
.sonar-welcome {
  display: flex;
  align-items: center;
  position: relative;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: var(--glass-mid-gray), var(--black);
  color: var(--white);
  &__container {
    display: flex;
    flex-direction: column;
    padding: 100px 30px 50px;
    max-width: 699px;
  }
  &__title {
    font-size: rem(24);
    line-height: rem(32);
    margin-bottom: 40px;
    font-family: var(--font-bold);
  }
  &__data-container {
    display: flex;
    margin-bottom: 40px;
  }
  &__data-card {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-width: 162px;
    gap: 17px;
    padding: 8px 24px;
    border-right: 2px solid var(--perfect-gray);
    &:last-child {
      border: none;
    }
  }
  &__data-title {
    font-size: rem(16);
    line-height: rem(24);
    color: var(--purple-pale);
    margin: 0;
  }
  &__data-info {
    font-size: rem(24);
    line-height: rem(32);
    font-weight: 700;
    margin: 0;
    text-transform: capitalize;
  }
  &__info-container {
    h4,
    p {
      font-size: rem(16);
      line-height: rem(23);
      margin-bottom: 10px;
    }
    h4 {
      margin: 0 0 10px;
    }
    ol {
      line-height: rem(23);
      padding-left: 0;
      counter-reset: li;
      list-style: none;
      margin-top: 20px;
    }

    ol li {
      counter-increment: li;
      margin-bottom: 10px;
      margin-bottom: 5px;
    }

    ol li::before {
      content: counter(li) ' .';
      margin-right: 5px;
    }
  }
  &__divider {
    height: 1px;
    width: 100%;
    background: var(--tundora-gray);
    margin: 20px 0;
  }

  &__top-skill-container {
    margin-top: 10px;
    margin-bottom: 10px;
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
  }

  &__top-skill {
    display: inline-block;
    width: auto;
  }
  &__infoItem-description {
    color: var(--light-blue-gray);
  }
}
</style>
